import { useState } from 'react';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { BrevoList, useBrevoLists, useDeleteBrevoList } from '../../queries/brevoLists';
import { BrevoListFormDialog } from './BrevoListFormDialog';
import { BrevoListSubscribersDialog } from './BrevoListSubscribersDialog';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

export function BrevoListsPage() {
  const { data: lists = [], isLoading } = useBrevoLists({ all: true });
  const remove = useDeleteBrevoList();
  const { success, error } = useToast();

  const [editingList, setEditingList] = useState<BrevoList | null | 'new'>(null);
  const [viewingSubscribers, setViewingSubscribers] = useState<BrevoList | null>(null);
  const [deleting, setDeleting] = useState<BrevoList | null>(null);

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.name}".`);
        setDeleting(null);
      },
      onError: (err) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const columns: DataTableColumn<BrevoList>[] = [
    { key: 'name', header: 'Name', priority: true, render: (l) => <span className="font-medium">{l.name}</span> },
    { key: 'type', header: 'Type', priority: false, render: (l) => <span className="text-muted-foreground @2xl:text-foreground">{l.listType}</span> },
    { key: 'subs', header: 'Subscribers', priority: true, alignEnd: true, render: (l) => <span className="tabular-nums">{l.subscriberCount.toLocaleString()}</span> },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (l) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => setViewingSubscribers(l)} title="Subscribers"><Users className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setEditingList(l)} title="Edit"><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleting(l)} className="text-muted-foreground hover:text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Email lists synced with Brevo.</p>
        <Button onClick={() => setEditingList('new')}>
          <Plus className="h-4 w-4" />
          Create list
        </Button>
      </div>

      {isLoading ? <Spinner /> : <DataTable columns={columns} data={lists} getRowKey={(l) => String(l.id)} emptyMessage="No lists yet." />}

      {editingList && <BrevoListFormDialog list={editingList === 'new' ? null : editingList} onClose={() => setEditingList(null)} />}
      {viewingSubscribers && <BrevoListSubscribersDialog list={viewingSubscribers} onClose={() => setViewingSubscribers(null)} />}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete list?"
        description={`"${deleting?.name}" will be removed from Brevo. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
